/*global jQuery:readonly */
import Bloodhound from "bloodhound-js";
import * as H from "history";
import * as React from "react";
import { NotificationManager } from "react-notifications";

import { IUserSearchHit, Stat, StatField, StatSummary } from "../data";
import { notifyServerErrorOrDefault } from "../helpers";

interface StatEntryEditorProps {
    stat: Stat;
    onClose: () => void;
    onUpdate: (stat: Stat) => void;
}

class StatEntryEditor extends React.Component<StatEntryEditorProps, Stat> {
    constructor(props: StatEntryEditorProps) {
        super(props);

        this.state = this.props.stat;

        this.handleCancel = this.handleCancel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    get fields(): JSX.Element[] {
        return this.state.fields.map(([ major, fields ]): JSX.Element => {
            const subfields = fields.map((field): JSX.Element => {
                return (
                    <div className="form-group" key={ field.key }>
                        <label htmlFor={ field.key }>{field.longDescription || field.description}</label>
                        <input
                            type="text"
                            name={ field.key }
                            className="form-control"
                            onChange={ (e): void => this.handleChange(field, e) }
                            value={ field.value }
                        />
                    </div>
                );
            });

            return (
                <div key={ major }>
                    <h3>{major}</h3>
                    {subfields}
                    <hr />
                </div>
            );
        });
    }

    handleCancel(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.props.onClose();
    }

    handleChange(field: StatField, e: React.SyntheticEvent<HTMLInputElement>): void {
        const target = e.currentTarget;
        const value = +target.value;
        this.setState((state: Stat): Stat => {
            field.value = value;

            // create a new object to flag change detection
            return { ...state, fields: state.fields.slice() };
        });
    }

    handleSave(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();

        const update = {};
        this.state.fields.forEach(([ , fields ]): void => {
            fields.forEach((field): void => { update[field.key] = field.value; });
        });

        jQuery.post(`/backend/toolbox/users/${this.state.userId}/stats/${this.state.id}`, update).then(
            (data): void => {
                NotificationManager.success("Updated stats");
                this.props.onUpdate({
                    changedFields: data.stat.changed_fields,
                    date: new Date(data.stat.date * 1000),
                    fields: data.stat.fields.map(([ major, fields ]): [ string, Array<any> ] => {
                        return [ major, fields.map((field): any => {
                            const { description, key, long_description: longDescription, unit, value } = field;
                            return { description, key, longDescription, unit, value };
                        }) ];
                    }),
                    id: data.stat.id,
                    logId: data.stat.log_id,
                    userId: data.stat.user_id
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, "Could not update stats");
            });
    }

    render(): JSX.Element | null {
        return (
            <form onSubmit={ this.handleSave }>
                {this.fields}

                <div className="form-group">
                    <div className="btn-group" role="group" aria-label="Stat actions">
                        <button type="submit" className="btn btn-primary">Save</button>
                        <button type="button" className="btn btn-default" onClick={ this.handleCancel }>Cancel</button>
                    </div>
                    <div className="btn-group pull-right">
                        <a
                            className="btn btn-default"
                            target="_blank"
                            href={ `/r/?page=log&logid=${this.props.stat.logId}` }
                            rel="noopener noreferrer"
                        >
                            <span className="glyphicon glyphicon-eye-open" /> View log entry
                        </a>
                    </div>
                </div>
            </form>
        );
    }
}

interface StatEditorProps {
    statId: string | undefined;
    userId: string | undefined;
    history: H.History;
}

interface StatEditorState {
    loading: boolean;
    stat: Stat | null;
    statId: number | null;
    statSummaries: StatSummary[];
    userId: number | null;
}

class StatEditor extends React.Component<StatEditorProps, StatEditorState> {
    state: StatEditorState;

    private agentSearch = new Bloodhound({
        identify: (obj): any => obj.id,
        datumTokenizer: Bloodhound.tokenizers.obj.whitespace("name"),
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        indexRemote: true,
        remote: {
            url: "/backend/agent?q=%QUERY",
            wildcard: "%QUERY"
        }
    });

    constructor(props: StatEditorProps) {
        super(props);
        this.state = {
            loading: false,
            stat: null,
            statId: props.statId && !isNaN(+props.statId) ? +props.statId : null,
            statSummaries: [],
            userId: props.userId && !isNaN(+props.userId) ? +props.userId : null
        };

        this.handleCloseStat = this.handleCloseStat.bind(this);
        this.handleLookupEvent = this.handleLookupEvent.bind(this);

        if (this.state.userId) {
            this.lookupUserStatSummaries(this.state.userId);

            if (this.state.statId) {
                this.lookupStat(this.state.userId, this.state.statId);
            }
        }
    }

    get stat(): JSX.Element | null {
        if (this.state.loading) {
            return (
                <div className="row">
                    <div className="col-xs-12">
                        <p>Loading...</p>
                    </div>
                </div>
            );
        }

        if (this.state.stat === null) {
            return null;
        }

        return (
            <div className="row">
                <div className="col-xs-12">
                    <StatEntryEditor
                        stat={ this.state.stat }
                        onClose={ this.handleCloseStat }
                        onUpdate={ (s: Stat): void => this.setState({ stat: s }) }
                    />
                </div>
            </div>
        );
    }

    get statOptions(): JSX.Element[] {
        return this.state.statSummaries.map((summary): JSX.Element => {
            return (
                <option value={ summary.id } key={ summary.id }>
          [#{summary.id}] {summary.date.toLocaleString()}
                </option>
            );
        });
    }

    componentDidMount(): void {
        const input = jQuery("#user");
        input.typeahead(
            {
                minLength: 2,
                highlight: true,
                hint: false
            },
            {
                async: true,
                displayKey: "name",
                limit: 10,
                source: this.agentSearch.ttAdapter()
            });
        input.on("typeahead:select", (event, item): void => {
            this.setState({
                statId: null,
                statSummaries: [],
                userId: +item.id
            });
            this.lookupUserStatSummaries(+item.id);
        });
        input.on("typeahead:change", (event, currentName): void => {
            this.agentSearch.search(currentName, (): void => {},
                (userSearchHits: IUserSearchHit[]): void => {
                    const foundUser = userSearchHits.find(({ name }: IUserSearchHit): boolean =>
                        currentName === name);
                    if (!foundUser) {
                        this.setState({
                            statId: null,
                            statSummaries: [],
                            userId: null
                        });
                    }
                }
            );
        });

        jQuery("#stat").selectpicker();
        if (this.state.statId !== null) {
            jQuery("#stat").selectpicker("val", String(this.state.statId));
        }

        jQuery("#stat").on("changed.bs.select", (e): void => {
            this.setState({
                statId: +e.target.value
            });
        });
    }

    componentDidUpdate(prevProps: StatEditorProps, prevState: StatEditorState): void {
        if (prevState.statSummaries !== this.state.statSummaries) {
            jQuery("#stat").selectpicker("refresh");

            if (this.state.statId) {
                jQuery("#stat").selectpicker("val",  String(this.state.statId));
            }
        }

        if (prevProps.statId !== this.props.statId ||
        prevProps.userId !== this.props.userId) {

            const statId = this.props.statId && !isNaN(+this.props.statId) ? +this.props.statId : null;
            const userId = this.props.userId && !isNaN(+this.props.userId) ? +this.props.userId : null;
            this.setState({ statId, userId });
            if (userId) {
                this.lookupUserStatSummaries(userId);

                if (statId) {
                    this.lookupStat(userId, statId);
                }
            }
        }
    }

    handleCloseStat(): void {
        this.setState({ stat: null });
    }

    handleLookupEvent(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        this.props.history.push(`/toolbox/stateditor/${this.state.userId}/${this.state.statId}`);
        const { userId, statId } = this.state;
        this.lookupStat(userId, statId);
    }

    lookupUserStatSummaries(userId: number): void {
        jQuery.getJSON(`/backend/toolbox/users/${userId}/stats`).then(
            (data): void => {
                this.setState((state: StatEditorState): StatEditorState => {
                    if (userId === state.userId) {
                        jQuery("#user").typeahead("val", data.user.codename);

                        return {
                            ...state,
                            statSummaries: data.stat_summaries.map((s): any => {
                                return {
                                    date: new Date(s.date * 1000),
                                    id: s.id,
                                    logId: s.log_id,
                                    userId: s.user_id
                                };
                            })
                        };
                    }

                    return {};
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not retrieve stats for user with id ${userId}`);
            });
    }

    lookupStat(userId: number, statId: number): void {
        this.setState({ loading: true });
        jQuery.getJSON(`/backend/toolbox/users/${userId}/stats/${statId}`).then(
            (data): void => {
                this.setState((state: StatEditorState): StatEditorState => {
                    if (userId === state.userId && statId === state.statId) {
                        // because the stat is looked up by `id` or `log_id` this may need to be set
                        jQuery("#stat").selectpicker("val", String(data.stat.id));

                        return {
                            ...state,
                            loading: false,
                            stat: {
                                changedFields: data.stat.changed_fields,
                                date: new Date(data.stat.date * 1000),
                                fields: data.stat.fields.map(([ major, fields ]): [ string, Array<any> ] => {
                                    return [ major, fields.map((field): any => {
                                        const { description, key,  long_description: longDescription, unit, value } = field;
                                        return { description, key, longDescription, unit, value };
                                    }) ];
                                }),
                                id: data.stat.id,
                                logId: data.stat.log_id,
                                userId: data.stat.user_id
                            }
                        };
                    }

                    return {};
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not retrieve stats for user with id ${userId}`);
            });
    }

    render(): JSX.Element {
        const isStatSelected = (this.state.userId !== null
        && this.state.statId !== null);
        const isStatLoaded = (this.state.stat
      && (this.state.statId === this.state.stat.id
        || this.state.statId === this.state.stat.logId));

        return (
            <div>
                <div className="row" id="header">
                    <div className="col-xs-12">
                        <h2>Stat Editor</h2>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <form onSubmit={ this.handleLookupEvent }>
                            <div className="row">
                                <div className="form-group col-xs-3">
                                    <div>
                                        <input
                                            id="user"
                                            type="text"
                                            autoComplete="off"
                                            className="form-control typeahead"
                                            placeholder="Type an agent name..."
                                        />
                                    </div>
                                </div>

                                <div className="form-group col-xs-5">
                                    <select
                                        id="stat"
                                        className="form-control"
                                        data-live-search="true"
                                        title="Stat Entry"
                                        data-size="7"
                                    >
                                        {this.statOptions}
                                    </select>
                                </div>

                                <div className="form-group col-xs-2">
                                    <button
                                        type="submit"
                                        className="btn btn-default"
                                        disabled={ !isStatSelected }
                                    >
                                        {(isStatLoaded) ? "Reload Stat" : "Open Stat"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12"><hr /></div>
                </div>

                {this.stat}
            </div>
        );
    }
}

export default StatEditor;
